import { createContext, useContext, useEffect, useState } from "react";
import { useLocalForage } from "../hooks/useLocalForage";
import { useUploadImages } from "../api/useUploadImages";
import { useConnectionStatus } from "../hooks/useConnectionStatus";
import { useUpdateTakeback } from "api/tes/useUpdateTakeback";
import { useAddStatus } from "api/tes/useAddStatus";
import AuthContext from "../contexts/AuthContext";

export const TakebackQueueContext = createContext({});

export const TakebackQueueProvider = ({ children }) => {
  const [queue, setQueue] = useLocalForage("takebackQueue");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOnline = useConnectionStatus();

  const { mutateAsync: uploadImages } = useUploadImages({
    timeout: 180000,
    retry: false,
  });
  const { mutateAsync: updateTakeback } = useUpdateTakeback({
    // retry: false,
    // timeout: 60000,
    // useV2: true,
  });
  const { mutateAsync: addStatusAsync } = useAddStatus();
  const { authTokens } = useContext(AuthContext);

  const isAuthenicated = !!authTokens;

  useEffect(() => {
    if (!isSubmitting && isOnline && isAuthenicated && queue?.length > 0) {
      setIsSubmitting(true);

      const submitTakeback = async ({
        caseId,
        imagesToUpload = [],
        ignore412 = false,
        takebackUpdates = [],
        statuses = [],
      } = {}) => {
        // Process all takeback updates
        if (takebackUpdates && takebackUpdates.length > 0) {
          for (const update of takebackUpdates) {
            await updateTakeback({
              id: caseId,
              input: update.input,
            });
          }
        }

        // Add any statuses
        if (statuses && statuses.length > 0) {
          for (const status of statuses) {
            await addStatusAsync({
              id: caseId,
              input: {
                status: status.status,
                parent_status: status.parent_status,
                updated_at: status.updated_at,
              },
            });
          }
        }

        // Upload images
        if (Object.keys(imagesToUpload).length > 0) {
          await uploadImages({
            caseId,
            imagesMap: imagesToUpload,
            ignore412,
          });
        }
      };

      const nextTakebackSubmission = queue[0];

      submitTakeback({ ...nextTakebackSubmission, ignore412: true })
        .then(() => {
          // success so remove it from the queue now
          setQueue(
            queue.filter(
              (takebackSubmission) =>
                takebackSubmission !== nextTakebackSubmission
            )
          );
        })
        .catch(() => {
          setQueue([...queue.slice(1)]);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  }, [
    isOnline,
    isSubmitting,
    queue,
    setQueue,
    updateTakeback,
    uploadImages,
    addStatusAsync,
    isAuthenicated,
  ]);

  const add = (takebackSubmission) => {
    // EDGE-CASE, should the takeback caseId already be in the queue, then ignore this new one because it means
    // the user is trying to re-do a takeback which isn't allowed!
    if (!queue?.some(({ caseId }) => caseId === takebackSubmission.caseId)) {
      setQueue([...(queue ?? []), takebackSubmission]);
    }
  };

  return (
    <TakebackQueueContext.Provider value={{ add, queueCount: queue?.length }}>
      {children}
    </TakebackQueueContext.Provider>
  );
};
